import { z } from '@/libs/zod';
import { LuckySequencyType, PrizeType, RaffleStatus } from '@/types/enums';

export const customerContactSchema = z.object({
  name: z.string(),
  email: z.string().email(),
  phone: z.string(),
});

export const customerDocumentSchema = customerContactSchema.extend({
  customerId: z.string().uuid(),
  hasAccount: z.boolean(),
  hasMail: z.boolean(),
  hasAddress: z.boolean(),
  socialName: z.string().optional(),
});

export const customerBalanceResponseSchema = z.object({
  totalBalance: z.number(),
  pendingBalance: z.number(),
  availableForWithdrawal: z.number(),
  name: z.string(),
  socialName: z.string().optional(),
});

export const customerWinnersFeedSchema = z.object({
  sequence: z.string(),
  name: z.string(),
  value: z.number(),
  date: z.string(),
  type: PrizeType,
  userName: z.string(),
  userSocialName: z.string().optional(),
  userCpf: z.string(),
  userPhone: z.string(),
  state: z.string().optional(),
  winnerVideoUrl: z.string().optional(),
});

export const customerWinnersFeedResponseSchema = z.object({
  data: z.array(customerWinnersFeedSchema),
  totalCount: z.number(),
  ticketsToScratch: z.number(),
  prizeValues: z.array(z.string()),
  user: customerContactSchema,
  urlScratcherBanner: z.string(),
});

export const customerUserInfoSchema = z.object({
  name: z.string(),
  cpf: z.string(),
  phone: z.string(),
});

export const availablePrizeSchema = z.object({
  prizeValue: z.number(),
  name: z.string(),
  type: PrizeType,
});

export const cartRaffleSchema = z.object({
  id: z.string(),
  createdAt: z.string(),
  ticketsAmount: z.number(),
  totalPrice: z.number(),
  winning: z.boolean(),
  cartId: z.string(),
  cartStatus: z.string(),
});

export const purchasedRaffleModelSchema = z.object({
  luckySequencyType: LuckySequencyType,
  free: z.boolean(),
});

export const ticketPrizeSchema = z.object({
  name: z.string().nullable(),
  type: PrizeType.nullable(),
  isDrawn: z.boolean().nullable(),
  prizeValue: z.number().nullable(),
});

export const ticketSchema = z.object({
  sequence: z.string(),
  winning: z.boolean(),
  prizes: z.array(ticketPrizeSchema),
  date: z.string(),
  id: z.string(),
  hasBeenScratched: z.boolean(),
});

export const ticketsByRaffleResponseSchema = z.object({
  urlScratcherBanner: z.string().nullable(),
  data: z.array(ticketSchema),
  prizeValues: z.array(z.number()),
  prizeNames: z.array(z.string()),
  totalCount: z.number(),
  ticketsToScratch: z.number(),
  user: customerUserInfoSchema,
});

export const purchasedRaffleResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  numbering: z.number(),
  status: RaffleStatus,
  drawDate: z.string(),
  urlThumbnail: z.string(),
  urlWhatsapp: z.string().nullable(),
  urlFacebook: z.string().nullable(),
  redirectRaffleUrl: z.string().nullable(),
  maxTicketsPerCpf: z.number().nullable(),
  urlScratcherBanner: z.string().nullable(),
  model: purchasedRaffleModelSchema,
  prizes: z.array(availablePrizeSchema),
  cartRaffle: z.array(cartRaffleSchema),
  availablePrizes: z.array(availablePrizeSchema),
  ticketsBought: z.number(),
  lastPurchase: z.string(),
  user: customerUserInfoSchema,
});

export type CustomerGuest = z.infer<typeof customerContactSchema>;
export type CustomerBalanceResponse = z.infer<typeof customerBalanceResponseSchema>;
export type CustomerWinnersFeedResponse = z.infer<
  typeof customerWinnersFeedResponseSchema
>;
export type CustomerGuestDocument = z.infer<typeof customerDocumentSchema>;
export type PurchasedRaffleResponse = z.infer<typeof purchasedRaffleResponseSchema>;
export type TicketsByRaffleResponse = z.infer<typeof ticketsByRaffleResponseSchema>;
export type Ticket = z.infer<typeof ticketSchema>;
export type WinnerType = z.infer<typeof customerWinnersFeedSchema>;
